import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { getPartnerId } from 'src/selectors/user'
import { TeamMemberStatus } from 'src/constants/team/team-members/enums'
import { TeamMember, TeamMemberDetails } from 'src/interfaces/team'
import { handleApiError } from 'src/utils/errorHandlers'
import { FetchTeamMember, FetchTeamMembers } from 'src/constants/actionTypes'
import { TeamContext } from '../../Team.context'
import { TeamTableHeader } from '../../grid/TeamTableHeader'
import { ITeamMembersTableContext, TeamMembersTableContext } from './TeamMembersTable.context'
import {
  teamMembersActionColumn,
  teamMembersBaseColumns,
} from 'src/constants/team/team-members/teamMembersTableGrid'
import { TeamMemberActionMenu } from './TeamMemberActionMenu'
import { TeamMemberDetailsModal } from '../../team-member-details/TeamMemberDetailsModal'
import { getTeamMembersCount } from 'src/selectors/team'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { useFormatMarketsData } from 'src/utils/hooks/useFormatMarketsData'

interface FetchTeamMembersParams {
  searchQuery: string
  sortModel?: GridSortModel
}

const getRowId = (row: TeamMember) => row.id

const TeamMembersTableComponent = () => {
  const dispatch = useDispatch()

  const teamMembersCount = useSelector(getTeamMembersCount) || 0
  const partnerId = useSelector(getPartnerId)

  const { hasPermission } = useCheckPermissions()
  const formatMarkets = useFormatMarketsData()

  const { searchQuery } = useContext(TeamContext)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
  const [teamMemberToAction, setTeamMemberToAction] = useState<TeamMember | null>(null)
  const [statusFilter, setStatusFilter] = useState<Array<TeamMemberStatus>>([])
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<string | null>(null)
  const [teamMemberDetails, setTeamMemberDetails] = useState<TeamMemberDetails | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'userName', sort: 'asc' }])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const fetchTeamMembers = useCallback(
    async ({ searchQuery, sortModel }: FetchTeamMembersParams) => {
      if (!partnerId) {
        return
      }

      setIsLoading(true)

      try {
        const params: { search?: string; disabled?: boolean; sort?: string } = {}

        if (searchQuery) {
          params.search = searchQuery
        }

        if (statusFilter.length === 1) {
          params.disabled = statusFilter[0] === TeamMemberStatus.Deactivated
        }

        if (sortModel && sortModel.length > 0) {
          const order = sortModel[0].sort?.toUpperCase()

          params.sort = `firstName:${order}`
        }

        const payload: { data: TeamMember[] } = await createAsyncAction(
          dispatch,
          FetchTeamMembers.request(params),
        )

        setTeamMembers(payload.data)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [partnerId, statusFilter, sortModel],
  )

  const refreshTeamMembers = useCallback(async () => {
    await fetchTeamMembers({ searchQuery, sortModel })
  }, [searchQuery, sortModel])

  useEffect(() => {
    if (partnerId) {
      fetchTeamMembers({ searchQuery, sortModel })
    }
  }, [partnerId, searchQuery, sortModel, statusFilter])

  const openTeamMemberItemAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, teamMemberToEdit: TeamMember) => {
      setActionAnchor(event.currentTarget)
      setTeamMemberToAction(teamMemberToEdit)
    },
    [],
  )

  const closeTeamMemberItemAction = useCallback(() => {
    setActionAnchor(null)
  }, [])

  const clearTeamMemberToAction = useCallback(() => {
    setTeamMemberToAction(null)
  }, [])

  const teamMembersContextValue = useMemo<ITeamMembersTableContext>(() => {
    return {
      refreshTeamMembers,

      statusFilter,
      setStatusFilter,

      actionAnchor,
      teamMemberToAction,
      openTeamMemberItemAction,
      closeTeamMemberItemAction,
      clearTeamMemberToAction,
    }
  }, [
    refreshTeamMembers,

    statusFilter,
    setStatusFilter,

    actionAnchor,
    teamMemberToAction,
    openTeamMemberItemAction,
    closeTeamMemberItemAction,
    clearTeamMemberToAction,
  ])

  const handleRowClick = useCallback(({ row }: { row: TeamMember }) => {
    setSelectedTeamMemberId(row.id)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedTeamMemberId(null)
  }, [])

  const fetchTeamMemberDetails = useCallback(async (teamMemberId: string) => {
    try {
      const teamMember: TeamMemberDetails = await createAsyncAction(
        dispatch,
        FetchTeamMember.request({ teamMemberId }),
      )

      setTeamMemberDetails(teamMember)
    } catch (err) {
      handleApiError(err)
    }
  }, [])

  const refreshTeamMemberDetails = useCallback(async () => {
    if (selectedTeamMemberId) {
      await fetchTeamMemberDetails(selectedTeamMemberId)
    }

    refreshTeamMembers()
  }, [selectedTeamMemberId, refreshTeamMembers])

  useEffect(() => {
    if (!selectedTeamMemberId) {
      setTeamMemberDetails(null)
    } else {
      fetchTeamMemberDetails(selectedTeamMemberId)
    }
  }, [selectedTeamMemberId])

  const columns = useMemo(() => {
    const cols = teamMembersBaseColumns.map((col) => {
      if (col.field === 'markets') {
        return {
          ...col,
          valueGetter: (params: any) => {
            if (params.row.allMarkets) {
              return 'All markets'
            }

            return formatMarkets(params.row.markets).join(', ')
          },
        }
      }

      return col
    })

    if (hasPermission(UserPermission.UsersEdit)) {
      cols.push(teamMembersActionColumn)
    }

    return cols
  }, [hasPermission, formatMarkets])

  return (
    <TeamMembersTableContext.Provider value={teamMembersContextValue}>
      <TeamTableHeader isShowStatusSelect />
      <GridTable<TeamMember>
        rows={teamMembers}
        rowHeight={66}
        columns={columns}
        isLoading={isLoading}
        sortModel={sortModel}
        rowCount={teamMembersCount}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
        getRowId={getRowId}
      />
      <TeamMemberActionMenu />
      {teamMemberDetails && (
        <TeamMemberDetailsModal
          teamMemberDetails={teamMemberDetails}
          onClose={handleCloseDetails}
          onRefresh={refreshTeamMemberDetails}
        />
      )}
    </TeamMembersTableContext.Provider>
  )
}

export const TeamMembersTable = memo(TeamMembersTableComponent)
